'use client';

import { notificationTipoLabel } from '@slims/shared/constants';
import { Badge, Button, Grid, Spinner, Stack, Text } from '@slims/ui/atoms';
import {
	ConfirmationDialog,
	DetailField,
	DetailTextarea,
	DetailTextInput,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	QuoteBlock,
	StatusBlock,
} from '@slims/ui/molecules';
import { NotificationCard } from '@slims/ui/organisms';
import { PageContainer, PageHeader, PageSection } from '@slims/ui/templates';
import { Bell, CheckCheck, Send } from 'lucide-react';
import { useRouter, useSearchParams } from '@/lib/next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AiRecommendationsPanel } from '@/components/myslims/ai-recommendations-panel';
import type { DBNotification } from '@/hooks/use-notifications';
import { useEnergyData } from '@/hooks/use-energy-data';
import { useNotifications } from '@/hooks/use-notifications';
import { useSystemAlerts } from '@/hooks/use-system-alerts';
import { useContextOrg } from '@/hooks/use-context-org';
import { useCERStore } from '@/stores/cer-store';

function fmtDateTime(iso: string | null): string {
	return iso
		? new Date(iso).toLocaleString('it-IT', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			})
		: '-';
}

/**
 * Un socio può eliminare solo le notifiche della propria casella: le
 * comunicazioni inviate a tutta la configurazione (`memberId` nullo) sono
 * condivise e restano in sola lettura, come lato API.
 */
function canMemberDelete(n: DBNotification): boolean {
	return n.memberId !== null;
}

function NotifCard({
	n,
	onClick,
	onDelete,
	sent,
}: {
	n: DBNotification;
	onClick: () => void;
	onDelete: () => void;
	sent?: boolean;
}) {
	return (
		<NotificationCard
			title={n.titolo}
			typeLabel={notificationTipoLabel(n.tipo, n.requestId)}
			message={n.messaggio}
			dateLabel={fmtDateTime(n.createdAt)}
			unread={!n.letto}
			sent={sent}
			onClick={onClick}
			onDelete={canMemberDelete(n) ? onDelete : undefined}
		/>
	);
}

function NotificheContent() {
	const {
		notifications,
		isLoading,
		error,
		markAsRead,
		markAllAsRead,
		createNotification,
		deleteNotification,
	} = useNotifications();
	const searchParams = useSearchParams();
	const router = useRouter();

	const config = useCERStore((s) => s.config);
	const contextOrg = useContextOrg();
	const tenantId = contextOrg?.tenantId ?? config.nome;
	const { totals } = useEnergyData(undefined, contextOrg?.orgId);
	const condivisaMensile = totals.totalCondivisa > 0 ? Math.round(totals.totalCondivisa / 12) : 0;
	const systemAlerts = useSystemAlerts(contextOrg?.orgId, 'member');

	const [detail, setDetail] = useState<DBNotification | null>(null);
	const [composeOpen, setComposeOpen] = useState(false);
	const [composeTitolo, setComposeTitolo] = useState('');
	const [composeMsg, setComposeMsg] = useState('');
	const [composeSaving, setComposeSaving] = useState(false);
	const [pendingDelete, setPendingDelete] = useState<DBNotification | null>(null);
	const [deleting, setDeleting] = useState(false);

	const GSE_TIPO = new Set<DBNotification['tipo']>([
		'expiry_alert',
		'missing_documents',
		'pod_anomaly',
	]);
	const isGSE = (n: DBNotification) =>
		GSE_TIPO.has(n.tipo) || /\bgse\b|scadenza|conguaglio/i.test(n.titolo);

	// Received from admin
	const received = notifications.filter((n) => n.recipientRole === 'cer_user');
	// Sent by member to admin
	const sent = notifications.filter((n) => n.recipientRole === 'cer_admin');
	// Only count unread from received (not member's own sent messages)
	const receivedUnreadCount = received.filter((n) => !n.letto).length;

	const receivedSorted = [...received].sort((a, b) => {
		if (!a.letto && b.letto) return -1;
		if (a.letto && !b.letto) return 1;
		const aGSE = isGSE(a) ? 0 : 1;
		const bGSE = isGSE(b) ? 0 : 1;
		if (aGSE !== bGSE) return aGSE - bGSE;
		return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
	});

	const sentSorted = [...sent].sort(
		(a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
	);

	const openId = searchParams.get('notifica');
	useEffect(() => {
		if (!openId || isLoading || notifications.length === 0) return;
		const target = notifications.find((n) => n.id === openId);
		if (!target) return;
		setDetail(target);
		if (!target.letto) markAsRead(target.id);
		router.replace('/notifiche', { scroll: false });
	}, [openId, isLoading, notifications, markAsRead, router]);

	function openDetail(n: DBNotification) {
		setDetail(n);
		if (!n.letto) markAsRead(n.id);
	}

	async function handleConfirmDelete() {
		if (!pendingDelete) return;
		setDeleting(true);
		try {
			await deleteNotification(pendingDelete.id);
			// La riga sparisce dalla lista: se era aperta nel dettaglio, chiudilo.
			setDetail((current) => (current?.id === pendingDelete.id ? null : current));
			toast.success('Notifica eliminata');
			setPendingDelete(null);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore eliminazione notifica');
		} finally {
			setDeleting(false);
		}
	}

	async function handleSendToAdmin(e: React.FormEvent) {
		e.preventDefault();
		if (!composeTitolo.trim()) {
			toast.error('Il titolo è obbligatorio');
			return;
		}
		setComposeSaving(true);
		try {
			await createNotification({
				titolo: composeTitolo.trim(),
				messaggio: composeMsg.trim() || null,
				tipo: 'manual',
				direction: 'inbound',
				memberId: null,
				canale: 'portale',
				letto: false,
				allegatoPath: null,
				allegatoNome: null,
				recipientRole: 'cer_admin',
				requestId: null,
			});
			toast.success("Messaggio inviato all'amministratore");
			setComposeOpen(false);
			setComposeTitolo('');
			setComposeMsg('');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore invio');
		} finally {
			setComposeSaving(false);
		}
	}

	return (
		<PageContainer width="narrow">
			<PageHeader
				title="Le mie notifiche"
				description="Comunicazioni dalla CER e messaggi inviati all'amministratore."
				actions={
					<>
						{receivedUnreadCount > 0 && (
							<Badge variant="orange">
								{receivedUnreadCount} non {receivedUnreadCount === 1 ? 'letta' : 'lette'}
							</Badge>
						)}
						{receivedUnreadCount > 0 && (
							<Button type="button" variant="outline" size="sm" onClick={() => markAllAsRead()}>
								<CheckCheck />
								Tutte lette
							</Button>
						)}
						<Button
							type="button"
							variant="accentSoft"
							size="sm"
							onClick={() => setComposeOpen(true)}
						>
							<Send />
							Invia messaggio all'Amministratore
						</Button>
					</>
				}
			/>

			{/* Raccomandazioni AI — stesse voci e comportamento Sì/No dell'app mobile */}
			<Stack mb="lg">
				<AiRecommendationsPanel
					tenantId={tenantId}
					condivisa={condivisaMensile}
					ctx={{
						nome: config.nome,
						soci: config.soci,
						kwp: config.kwp,
					}}
					onExplain={() => router.push('/myslims')}
					explainLabel="Apri AI Coach"
				/>
			</Stack>

			{isLoading ? (
				<StatusBlock kind="loading" size="lg" />
			) : error ? (
				<StatusBlock kind="error" size="lg" message={error} />
			) : (
				<Stack gap="xl">
					{/* Avvisi calcolati dai dati della configurazione: prima erano il
					    banner in cima a MySlims. */}
					<PageSection title="Notifiche di Sistema">
						{systemAlerts.length === 0 ? (
							<StatusBlock kind="empty" size="sm" message="Nessuna notifica di sistema." />
						) : (
							<Stack gap="sm">
								{systemAlerts.map((alert) => (
									<NotificationCard
										key={alert.id}
										title={`${alert.emoji} ${alert.title}`}
										typeLabel={alert.badge}
										message={alert.message}
										dateLabel="Stato configurazione"
										unread={alert.actionRequired}
										onClick={() => router.push(alert.href)}
									/>
								))}
							</Stack>
						)}
					</PageSection>

					{/* Received from admin */}
					<PageSection
						title="Notifiche ricevute"
						aside={
							receivedUnreadCount > 0 && (
								<Badge variant="orange">{receivedUnreadCount} non lette</Badge>
							)
						}
					>
						{receivedSorted.length === 0 ? (
							<StatusBlock kind="empty" size="md" icon={<Bell />} message="Nessuna notifica ricevuta." />
						) : (
							<Stack gap="sm">
								{receivedSorted.map((n) => (
									<NotifCard
										key={n.id}
										n={n}
										onClick={() => openDetail(n)}
										onDelete={() => setPendingDelete(n)}
									/>
								))}
							</Stack>
						)}
					</PageSection>

					{/* Sent to admin */}
					<PageSection title="Messaggi inviati all'admin">
						{sentSorted.length === 0 ? (
							<StatusBlock kind="empty" size="sm" message="Nessun messaggio inviato." />
						) : (
							<Stack gap="sm">
								{sentSorted.map((n) => (
									<NotifCard
										key={n.id}
										n={n}
										onClick={() => openDetail(n)}
										onDelete={() => setPendingDelete(n)}
										sent
									/>
								))}
							</Stack>
						)}
					</PageSection>
				</Stack>
			)}

			{/* Delete confirmation */}
			<ConfirmationDialog
				open={pendingDelete !== null}
				onOpenChange={(open) => !open && setPendingDelete(null)}
				title="Elimina notifica"
				description={
					pendingDelete
						? `"${pendingDelete.titolo}" verrà eliminata definitivamente. L'operazione non è reversibile.`
						: ''
				}
				confirmText="Elimina"
				isLoading={deleting}
				onConfirm={handleConfirmDelete}
			/>

			{/* Compose dialog */}
			<Dialog open={composeOpen} onOpenChange={setComposeOpen}>
				<DialogContent width="md" tone="card">
					<DialogHeader>
						<DialogTitle size="sm">Invia messaggio all'Amministratore</DialogTitle>
					</DialogHeader>
					<form onSubmit={handleSendToAdmin}>
						<Stack gap="md">
							<DetailField label="Oggetto *">
								<DetailTextInput
									value={composeTitolo}
									onChange={setComposeTitolo}
									placeholder="Es. Richiesta informazioni documenti"
								/>
							</DetailField>
							<DetailField label="Messaggio">
								<DetailTextarea
									value={composeMsg}
									onChange={setComposeMsg}
									rows={4}
									placeholder="Scrivi il tuo messaggio..."
								/>
							</DetailField>
							<DialogFooter>
								<Button type="button" variant="outline" onClick={() => setComposeOpen(false)}>
									Annulla
								</Button>
								<Button type="submit" variant="accentSoft" disabled={composeSaving}>
									{composeSaving && <Spinner size="sm" tone="inherit" />}
									Invia
								</Button>
							</DialogFooter>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>

			{/* Detail modal */}
			{detail && (
				<Dialog open onOpenChange={(open) => !open && setDetail(null)}>
					<DialogContent width="md" tone="card">
						<DialogHeader>
							<DialogTitle size="sm">{detail.titolo}</DialogTitle>
						</DialogHeader>
						<Stack gap="md">
							<Grid columns={2} gap="md">
								<div>
									<Text variant="label" tone="muted">
										Tipo
									</Text>
									<Text>{notificationTipoLabel(detail.tipo, detail.requestId)}</Text>
								</div>
								<div>
									<Text variant="label" tone="muted">
										Data
									</Text>
									<Text>{fmtDateTime(detail.createdAt)}</Text>
								</div>
								<div>
									<Text variant="label" tone="muted">
										Direzione
									</Text>
									<Text>
										{detail.recipientRole === 'cer_admin'
											? "→ Inviato all'admin"
											: "← Ricevuto dall'admin"}
									</Text>
								</div>
								<div>
									<Text variant="label" tone="muted">
										Stato
									</Text>
									<Text
										tone={detail.letto ? 'muted' : 'accent'}
										weight={detail.letto ? undefined : 'semibold'}
									>
										{detail.letto ? 'Letta' : 'Non letta'}
									</Text>
								</div>
							</Grid>
							{detail.messaggio && (
								<div>
									<Text variant="label" tone="muted">
										Messaggio
									</Text>
									<QuoteBlock>{detail.messaggio}</QuoteBlock>
								</div>
							)}
						</Stack>
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => setDetail(null)}>
								Chiudi
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}
		</PageContainer>
	);
}

export default function NotifichePage() {
	return (
		<Suspense>
			<NotificheContent />
		</Suspense>
	);
}
